import { Link, NavLink, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Show, useAuth } from "@/lib/auth";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LogOut, Bell, Camera, Home, MessagesSquare, ClipboardList, User, Map, Crown, LayoutDashboard, BookOpen, BarChart3 } from "lucide-react";
import { useMe } from "@/context/Me";
import { Tape } from "@/components/bits";

function navFor(me) {
  if (!me?.profile_type) return [
    { to: "/", label: "Início", icon: Home },
    { to: "/diagnostico", label: "Avaliação técnica", icon: Camera, big: true },
    { to: "/assistente", label: "Assistente", icon: MessagesSquare },
    { to: "/historia", label: "História", icon: BookOpen },
  ];
  if (me.profile_type === "pro") return [
    { to: "/mural", label: "Mural", icon: Map },
    { to: "/chamados", label: "Serviços", icon: ClipboardList },
    { to: "/assistente", label: "Assistente", icon: MessagesSquare, big: true },
    { to: "/planos", label: "Planos", icon: Crown },
    { to: "/perfil", label: "Perfil", icon: User },
  ];
  return [
    { to: "/inicio", label: "Início", icon: Home },
    { to: "/chamados", label: "Chamados", icon: ClipboardList },
    { to: "/diagnostico", label: "Avaliação técnica", icon: Camera, big: true },
    { to: "/assistente", label: "Assistente", icon: MessagesSquare },
    { to: "/perfil", label: "Perfil", icon: User },
  ];
}

function LocalUserMenu({ me }) {
  const { signOut } = useAuth();
  const initial = (me?.full_name || me?.name || me?.email || "?")[0].toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFC700] font-bold text-[#111317]">{initial}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[1100] border-white/10 bg-[#181B20] text-slate-100">
        <DropdownMenuLabel><div className="font-bold">{me?.full_name || me?.name}</div><div className="text-xs font-normal text-slate-400">{me?.email}</div></DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem asChild><Link to="/perfil" data-testid="user-menu-profile">Meu perfil</Link></DropdownMenuItem>
        <DropdownMenuItem data-testid="user-menu-signout" onClick={() => { signOut(); window.location.href = "/"; }}><LogOut size={14} className="mr-2" />Sair</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { mode } = useAuth();
  const { me, country, chooseCountry } = useMe();
  const items = navFor(me);
  const loc = useLocation();
  const isAdmin = me?.role === "admin";
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-[1000] border-b border-white/10 bg-[#111317]/85 backdrop-blur-xl">
        <Tape className="h-1.5" />
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <img src="/icon-192.png" alt="" className="h-9 w-9 rounded-lg" />
            <span className="font-display text-2xl font-black uppercase tracking-tight">Zel<span className="text-[#FFC700]">obra</span></span>
          </Link>
          {me?.country ? <span className="hidden rounded-full border border-white/10 px-2 py-0.5 text-xs text-slate-300 sm:inline" data-testid="country-chip">{me.country === "PT" ? "🇵🇹 Portugal" : "🇧🇷 Brasil"}</span>
            : <button type="button" title="Trocar país" onClick={() => chooseCountry(null)} data-testid="country-switch" className="rounded-full border border-white/10 px-2 py-0.5 text-xs text-slate-300 hover:border-[#FFC700]/60">{country === "PT" ? "🇵🇹" : "🇧🇷"}<span className="hidden sm:inline"> {country === "PT" ? "Portugal" : "Brasil"} · trocar</span></button>}
          <nav className="ml-4 hidden items-center gap-1 md:flex">
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end data-testid={`nav-${i.to.slice(1) || "home"}`}
                className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-[#FFC700] text-[#111317]" : "text-slate-300 hover:bg-white/5"}`}>
                {i.label}
              </NavLink>
            ))}
            {me && <NavLink to="/lojas" data-testid="nav-lojas" className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-semibold ${isActive ? "bg-[#FFC700] text-[#111317]" : "text-slate-300 hover:bg-white/5"}`}>Lojas</NavLink>}
            {me?.profile_type && <NavLink to="/relatorios" data-testid="nav-relatorios" className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-semibold ${isActive ? "bg-[#FFC700] text-[#111317]" : "text-slate-300 hover:bg-white/5"}`}>Relatórios</NavLink>}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {isAdmin && (
              <Link to="/admin" data-testid="nav-admin" className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-bold ${loc.pathname.startsWith("/admin") ? "border-[#FF5722] bg-[#FF5722] text-white" : "border-[#FF5722]/50 text-[#FF5722] hover:bg-[#FF5722]/10"}`}>
                <LayoutDashboard size={16} /><span className="hidden sm:inline">Painel</span>
              </Link>
            )}
            <Show when="signed-in">
              {me?.profile_type && <Link to="/relatorios" data-testid="mobile-reports-link" className="rounded-lg p-2 text-slate-300 hover:bg-white/5 md:hidden"><BarChart3 size={20} /></Link>}
              <Link to="/notificacoes" data-testid="notifications-bell" className="relative rounded-lg p-2 text-slate-200 hover:bg-white/5">
                <Bell size={22} />
                {me?.unread > 0 && <span data-testid="notifications-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF5722] px-1 text-[11px] font-bold text-white">{me.unread}</span>}
              </Link>
              {mode === "clerk" ? <UserButton /> : <LocalUserMenu me={me} />}
            </Show>
            <Show when="signed-out">
              <Link to="/sign-in" data-testid="header-signin-btn" className="btn-ghost px-4 py-2 text-sm">Entrar</Link>
              <Link to="/sign-up" data-testid="header-signup-btn" className="btn-hazard hidden px-4 py-2 text-sm sm:inline-flex">Criar conta</Link>
            </Show>
          </div>
        </div>
      </header>
      <main className="pb-safe">{children}</main>
      <nav className="fixed inset-x-0 bottom-0 z-[1000] border-t border-white/10 bg-[#111317]/95 backdrop-blur-xl md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="grid h-[68px]" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} end data-testid={`bottom-nav-${i.to.slice(1) || "home"}`} className="flex flex-col items-center justify-center gap-0.5">
              {({ isActive }) => i.big ? (
                <span className="-mt-7 flex h-14 w-14 items-center justify-center rounded-2xl border-4 border-[#111317] bg-[#FFC700] text-[#111317] shadow-[0_6px_24px_rgba(255,199,0,0.35)]"><i.icon size={24} /></span>
              ) : (
                <>
                  <i.icon size={22} className={isActive ? "text-[#FFC700]" : "text-slate-400"} />
                  <span className={`text-[11px] font-semibold ${isActive ? "text-[#FFC700]" : "text-slate-400"}`}>{i.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
